/**
 * Main entry point for customer data normalization.
 * Detects the integration source and delegates to the appropriate normalizer.
 */

import type { EnhancedDataItem } from "@/api/mariana-tek";
import type { Customer, CustomerEvent } from "@/api/types";
import { getCustomerSource } from "@/lib/utils";
import {
  normalizeFitdegreeActivity,
  normalizeFitdegreeMembership,
  normalizeFitdegreeSpend,
} from "./fitdegree";
import { normalizeMarianaTekActivity, normalizeMarianaTekMemberships } from "./mariana-tek";
import {
  normalizeMindbodyActivity,
  normalizeMindbodyMembership,
  normalizeMindbodySpend,
} from "./mindbody";
import { normalizeTablelistActivity, normalizeTablelistSpend } from "./tablelist";
import type { ActivityData, CustomerData, CustomerSource } from "./types";

/**
 * Integration-specific data that may be fetched separately.
 */
export interface IntegrationData {
  /** Mariana Tek user details from RPC */
  marianaTekDetails?: any;
  /** Mariana Tek memberships from RPC */
  marianaTekMemberships?: EnhancedDataItem[];
  /** Mariana Tek reservations from RPC */
  marianaTekReservations?: EnhancedDataItem[];
  /** Customer events from the CustomerEvent collection */
  customerEvents?: CustomerEvent[];
}

/**
 * Normalize customer data from any integration source into the unified CustomerData shape.
 *
 * @param customer - The customer object with properties from integrations
 * @param integrationData - Additional data fetched from integration APIs
 * @returns Normalized CustomerData for the UI
 */
export function normalizeCustomerData(
  customer: Customer | undefined,
  integrationData?: IntegrationData,
): CustomerData | null {
  if (!customer) {
    return null;
  }

  const sourceString = getCustomerSource(customer);
  const source = mapSourceToCustomerSource(sourceString);

  switch (source) {
    case "mariana-tek":
      return normalizeMarianaTek(customer, integrationData);
    case "mindbody":
      return normalizeMindbody(customer, integrationData?.customerEvents);
    case "tablelist":
      return normalizeTablelist(customer, integrationData?.customerEvents);
    case "fitdegree":
      return normalizeFitdegree(customer, integrationData?.customerEvents);
    default:
      return normalizeHelios(customer);
  }
}

/**
 * Map the source string from getCustomerSource to our CustomerSource type.
 */
function mapSourceToCustomerSource(source: string): CustomerSource {
  switch (source) {
    case "mariana-tek":
      return "mariana-tek";
    case "mindbody":
      return "mindbody";
    case "tablelist":
      return "tablelist";
    case "fitdegree":
      return "fitdegree";
    default:
      return "helios";
  }
}

/**
 * Normalize Mariana Tek customer data.
 */
function normalizeMarianaTek(customer: Customer, integrationData?: IntegrationData): CustomerData {
  const membership = normalizeMarianaTekMemberships(integrationData?.marianaTekMemberships);

  const activity = normalizeMarianaTekActivity(
    integrationData?.marianaTekReservations,
    integrationData?.marianaTekDetails,
  );

  return {
    source: "mariana-tek",
    membership,
    activity,
    spend: null, // Mariana Tek doesn't have spend data
  };
}

/**
 * Normalize Mindbody customer data.  Membership comes from the normalized
 * entitlement state on `customer.properties.mindbody` (with the legacy
 * `Status` / `MembershipStats` snapshot as fallback); activity and spend
 * combine the rollups with booking / order customerEvents.
 */
function normalizeMindbody(customer: Customer, events?: CustomerEvent[]): CustomerData {
  return {
    source: "mindbody",
    membership: normalizeMindbodyMembership(customer),
    activity: normalizeMindbodyActivity(customer, events),
    spend: normalizeMindbodySpend(customer, events),
  };
}

/**
 * Normalize Tablelist customer data.
 */
function normalizeTablelist(customer: Customer, events?: CustomerEvent[]): CustomerData {
  const properties = customer.properties?.tablelist;

  return {
    source: "tablelist",
    membership: null, // Tablelist doesn't have membership data
    activity: normalizeTablelistActivity(properties, events),
    spend: normalizeTablelistSpend(properties, events),
  };
}

/**
 * Normalize FitDEGREE customer data.  Memberships, spend, and activity
 * are all derived from `customer.properties.fitdegree.*` plus the
 * relevant customerEvents (registration_* for activity, sale_completed
 * for spend recent orders).
 */
function normalizeFitdegree(customer: Customer, events?: CustomerEvent[]): CustomerData {
  return {
    source: "fitdegree",
    membership: normalizeFitdegreeMembership(customer),
    activity: normalizeFitdegreeActivity(customer, events),
    spend: normalizeFitdegreeSpend(customer, events),
  };
}

/**
 * Normalize Helios-native customer data (no integration).
 */
function normalizeHelios(_customer: Customer): CustomerData {
  const emptyActivity: ActivityData = {
    upcoming: [],
    recent: [],
    totalCount: 0,
    lastActivityAt: null,
  };

  return {
    source: "helios",
    membership: null,
    activity: emptyActivity,
    spend: null,
  };
}

/**
 * Check if a customer has integration data worth displaying.
 */
export function hasIntegrationData(data: CustomerData | null): boolean {
  if (!data) return false;
  if (data.source === "helios") return false;

  return (
    data.membership !== null ||
    data.activity.totalCount > 0 ||
    data.activity.upcoming.length > 0 ||
    data.activity.recent.length > 0 ||
    data.spend !== null
  );
}
